import { useCallback, useEffect, useRef, useState } from 'react';
import { useTrip } from '../lib/tripContext.jsx';
import { useSession } from '../lib/sessionContext.jsx';
import { uploadTripRollPhoto, fetchTripRoll } from '../lib/tripRollApi.js';
import { compressForUpload } from '../lib/imageCompression.js';

// Item 4: video upload support. Cap matches the operator-confirmed
// bounds — 60s duration, 100 MB size. Both enforced client-side
// before upload; server has a duration backstop too.
const VIDEO_MAX_SECONDS = 60;
const VIDEO_MAX_BYTES = 100 * 1024 * 1024;

function classifyFile(file) {
  if (!file?.type) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  return 'image';
}

// Reads a video file's duration via a temporary <video> element +
// object URL. Resolves null if the browser can't decode it (we'll
// reject the upload upstream — better than letting a long clip slip
// through). The object URL is revoked on resolve to free memory.
function readVideoDurationSeconds(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.onloadedmetadata = () => {
      const d = video.duration;
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(d) ? d : null);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    video.src = url;
  });
}

// Trip Roll — off-challenge memory photos. Lower-friction than the
// Wilman flow: tap, pick, optional caption, commit. No verifier, no
// points, no Wilman commentary. Photos still feed the slideshow.
//
// Multi-select: iOS lets the player pick N photos in one go. When N>1
// we bypass the per-photo caption step and bulk-upload all at once
// (caption stays empty). When N=1 the existing flow stays, including
// the optional caption.

export default function TripRoll() {
  const { tripId, trip } = useTrip();
  const session = useSession();
  const inputRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingResourceType, setPendingResourceType] = useState('image');
  const [pendingDuration, setPendingDuration] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [caption, setCaption] = useState('');
  // 'idle' → 'compressing' → 'picked' (N=1) | 'bulk-uploading' (N>1) → 'uploading' → 'done' | 'error'
  const [stage, setStage] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0, failed: 0 });
  const [error, setError] = useState(null);
  const [records, setRecords] = useState([]);
  const [feedError, setFeedError] = useState(null);

  const meId = session?.role === 'player' ? session.recipient_id : null;
  const players = trip?.players || [];

  const loadFeed = useCallback(async () => {
    try {
      const res = await fetchTripRoll({ tripId });
      setRecords(res.records || []);
      setFeedError(null);
    } catch (err) {
      if (err.status === 'api_unavailable') {
        setRecords([]);
        return;
      }
      setFeedError(err.message || String(err));
    }
  }, [tripId]);

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  // Validate a single file (image OR video). Returns { ok, error,
  // resourceType, durationSeconds }. For video, enforces size + duration
  // caps client-side so we don't waste bandwidth on uploads the server
  // would reject.
  async function validateFile(raw) {
    const resourceType = classifyFile(raw);
    if (resourceType === 'video') {
      if (raw.size > VIDEO_MAX_BYTES) {
        return {
          ok: false,
          error: `Video too large (${Math.round(raw.size / 1024 / 1024)} MB > 100 MB cap)`,
        };
      }
      const duration = await readVideoDurationSeconds(raw);
      if (duration == null) {
        return { ok: false, error: 'Could not read video duration.' };
      }
      if (duration > VIDEO_MAX_SECONDS) {
        return {
          ok: false,
          error: `Video too long (${Math.round(duration)}s > ${VIDEO_MAX_SECONDS}s cap)`,
        };
      }
      return { ok: true, resourceType, durationSeconds: duration };
    }
    return { ok: true, resourceType, durationSeconds: null };
  }

  async function pickFiles(fileList) {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setError(null);

    // Multi-select path: bulk-upload all N, no caption flow.
    if (files.length > 1) {
      setStage('bulk-uploading');
      setBulkProgress({ done: 0, total: files.length, failed: 0 });
      let done = 0;
      let failed = 0;
      for (const raw of files) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const v = await validateFile(raw);
          if (!v.ok) {
            console.warn('[trip-roll bulk] file rejected:', v.error);
            failed += 1;
            continue;
          }
          // Skip image compression for video — compressForUpload is
          // image-only (browser-image-compression). Pass video file as-is.
          const file =
            v.resourceType === 'image'
              ? // eslint-disable-next-line no-await-in-loop
                await compressForUpload(raw)
              : raw;
          // eslint-disable-next-line no-await-in-loop
          await uploadTripRollPhoto({
            tripId,
            file,
            caption: '',
            resourceType: v.resourceType,
            durationSeconds: v.durationSeconds,
          });
        } catch (err) {
          console.warn('[trip-roll bulk] upload failed:', err);
          failed += 1;
        } finally {
          done += 1;
          setBulkProgress({ done, total: files.length, failed });
        }
      }
      setStage('done');
      reset();
      await loadFeed();
      return;
    }

    // Single-pick path: keep the caption preview flow.
    const raw = files[0];
    const v = await validateFile(raw);
    if (!v.ok) {
      setError(v.error);
      setStage('error');
      return;
    }
    setStage('compressing');
    setPreviewUrl(URL.createObjectURL(raw));
    setCaption('');
    setProgress(0);
    const file =
      v.resourceType === 'image' ? await compressForUpload(raw) : raw;
    setPendingFile(file);
    setPendingResourceType(v.resourceType);
    setPendingDuration(v.durationSeconds);
    setStage('picked');
  }

  function reset() {
    setPendingFile(null);
    setPendingResourceType('image');
    setPendingDuration(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setCaption('');
    setStage('idle');
    setProgress(0);
    setBulkProgress({ done: 0, total: 0, failed: 0 });
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  async function commit() {
    if (!pendingFile || !meId) return;
    setStage('uploading');
    setError(null);
    try {
      await uploadTripRollPhoto({
        tripId,
        file: pendingFile,
        caption,
        onProgress: (p) => setProgress(p),
        resourceType: pendingResourceType,
        durationSeconds: pendingDuration,
      });
      setStage('done');
      reset();
      await loadFeed();
    } catch (err) {
      setStage('error');
      setError(err.message || String(err));
    }
  }

  return (
    <div className="mx-auto w-full max-w-screen-sm space-y-4 p-4">
      <header>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
          off-challenge · feeds the slideshow
        </p>
        <h1 className="mt-1 font-serif text-2xl">Trip Roll</h1>
        <p className="mt-1 text-sm text-fg-secondary">
          Group dinners, the apartment view, candid moments. No Wilman, no points — just
          memory frames the slideshow remembers.
        </p>
      </header>

      {!session.loading && !meId && (
        <div className="card border-copper/40 bg-copper/5 px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-wider text-copper-dark">
            sign-in required
          </p>
          <p className="mt-1 text-sm text-fg-secondary">
            Open from your briefing-email link to upload memory frames.
          </p>
        </div>
      )}

      {meId && (
        <section
          aria-labelledby="upload"
          className="card overflow-hidden"
        >
          <header className="border-b border-border-tertiary bg-bg-secondary px-4 py-2">
            <h2
              id="upload"
              className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary"
            >
              add to roll
            </h2>
          </header>
          <div className="space-y-3 p-3">
            <input
              ref={inputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              className="sr-only"
              onChange={(e) => {
                pickFiles(e.target.files);
                // Allow re-picking the same file by clearing the value.
                e.target.value = '';
              }}
            />

            {previewUrl ? (
              pendingResourceType === 'video' ? (
                <video
                  src={previewUrl}
                  controls
                  playsInline
                  className="h-44 w-full rounded-md border border-border-tertiary object-cover"
                />
              ) : (
                <img
                  src={previewUrl}
                  alt="upload preview"
                  className="h-44 w-full rounded-md border border-border-tertiary object-cover"
                />
              )
            ) : (
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={stage === 'compressing' || stage === 'bulk-uploading'}
                className="flex h-32 w-full items-center justify-center rounded-md border border-dashed border-border-tertiary bg-bg-secondary px-3 text-center font-mono text-xs uppercase tracking-wider text-fg-secondary transition hover:bg-bg-tertiary disabled:opacity-60"
              >
                tap to pick photos or video clips · multi-select · ≤ 60s, ≤ 100 MB per video
              </button>
            )}

            {stage === 'compressing' && (
              <p className="font-mono text-[11px] uppercase tracking-wider text-fg-tertiary">
                compressing…
              </p>
            )}

            {stage === 'bulk-uploading' && (
              <p className="font-mono text-[11px] uppercase tracking-wider text-fg-tertiary">
                uploading {bulkProgress.done}/{bulkProgress.total}
                {bulkProgress.failed > 0 ? ` · ${bulkProgress.failed} failed` : ''}…
              </p>
            )}

            {stage === 'picked' && (
              <>
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  rows={2}
                  placeholder="caption (optional)"
                  maxLength={280}
                  className="w-full resize-y rounded-md border border-border-tertiary bg-bg-primary px-3 py-2 text-sm placeholder:text-fg-tertiary focus:border-action focus:outline-none"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={commit}
                    className="flex-1 rounded-md border border-action bg-action px-3 py-2 font-mono text-xs uppercase tracking-wider text-action-fg"
                  >
                    add to roll
                  </button>
                  <button
                    type="button"
                    onClick={reset}
                    className="rounded-md border border-border-tertiary bg-bg-secondary px-3 py-2 font-mono text-xs uppercase tracking-wider text-fg-secondary hover:bg-bg-tertiary"
                  >
                    discard
                  </button>
                </div>
              </>
            )}

            {stage === 'uploading' && (
              <p className="font-mono text-[11px] uppercase tracking-wider text-fg-tertiary">
                uploading… {Math.round(progress * 100)}%
              </p>
            )}
            {stage === 'done' && (
              <p className="rounded-md border border-success/40 bg-success/10 px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-success">
                added to the roll
              </p>
            )}
            {error && (
              <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
                {error}
              </p>
            )}
          </div>
        </section>
      )}

      <section aria-labelledby="feed" className="space-y-2">
        <h2
          id="feed"
          className="font-mono text-[10px] uppercase tracking-wider text-fg-secondary"
        >
          the roll · {records.length}
        </h2>
        {feedError && (
          <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
            {feedError}
          </div>
        )}
        {records.length === 0 ? (
          <div className="card px-4 py-6 text-center font-serif italic text-fg-secondary">
            Nothing in the roll yet.
          </div>
        ) : (
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {records.map((r) => {
              const player = players.find((p) => p.id === r.player_id);
              return (
                <li key={r.id} className="card overflow-hidden">
                  <div className="aspect-square bg-black">
                    {r.photo_url ? (
                      r.resource_type === 'video' ? (
                        <video
                          src={r.photo_url}
                          controls
                          playsInline
                          preload="metadata"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <img
                          src={r.photo_url}
                          alt={r.caption || ''}
                          className="h-full w-full object-cover"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                      )
                    ) : null}
                  </div>
                  <div className="space-y-0.5 px-2 py-1.5">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                      {(player?.display_name || player?.name || r.player_id)} · {formatRelative(r.uploaded_at)}
                    </p>
                    {r.caption && (
                      <p className="line-clamp-2 text-xs text-fg-secondary">{r.caption}</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

function formatRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const ms = Date.now() - d.getTime();
  const min = Math.round(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
}
